import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Settings, Save, ArrowLeft, Copy, CheckCircle2, AlertTriangle,
  MessageCircle, ExternalLink, Zap, Shield,
} from 'lucide-react';
import { toast } from 'sonner';

const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + '/api' });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const CRMSettingsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({
    app_id: '',
    app_secret: '',
    phone_number_id: '',
    waba_id: '',
    access_token: '',
    verify_token: '',
  });
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/config');
      setConfig(res.data);
      setForm({
        app_id: res.data.app_id || '',
        app_secret: res.data.app_secret && res.data.app_secret !== '' ? '••••••••' : '',
        phone_number_id: res.data.phone_number_id || '',
        waba_id: res.data.waba_id || '',
        access_token: res.data.access_token && res.data.access_token !== '' ? '••••••••' : '',
        verify_token: res.data.verify_token && res.data.verify_token !== '' ? '••••••••' : '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const webhookUrl = React.useMemo(() => {
    // Always compute at run time so preview + prod both show correctly
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return base ? `${base.replace(/\/$/, '')}/api/meta-whatsapp/webhook` : '';
  }, []);

  const copy = async (val, label) => {
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Send only what user actually changed. Leaving a masked field as-is → don't send.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v && !/^•+$/.test(v)) payload[k] = v.trim();
      });
      await api.put('/crm/config', payload);
      toast.success('Config saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await api.post('/crm/config/test');
      toast.success(`Connected to ${res.data.info?.verified_name || res.data.info?.display_phone_number || 'Meta WABA'}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Meta rejected these credentials');
    } finally {
      setTesting(false);
    }
  };

  const switchProvider = async (provider) => {
    setSwitching(true);
    try {
      await api.put('/crm/provider', { provider });
      toast.success(`Provider set to ${provider}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch provider');
    } finally {
      setSwitching(false);
    }
  };

  if (user?.role !== 'Admin') {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-8 text-center text-gray-500">Admin only.</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/crm')} data-testid="crm-settings-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Inbox
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-5 h-5 text-emerald-600" /> Meta WhatsApp Setup
            </h1>
            <p className="text-xs md:text-sm text-gray-500">Configure the Eden WABA app and switch away from Interakt when ready.</p>
          </div>
        </div>

        {/* Status card */}
        <Card>
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              {config?.configured ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              )}
              <div>
                <p className="font-semibold text-gray-900" data-testid="crm-status-text">
                  {config?.configured ? 'Ready to send & receive' : 'Not fully configured'}
                </p>
                <p className="text-xs text-gray-500">
                  {config?.display_phone_number
                    ? `Business: ${config.business_name || '—'} · +${config.display_phone_number}`
                    : 'Fill the credentials below and hit “Test connection”.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Active provider:</span>
              <div className="inline-flex rounded-lg bg-gray-100 p-1">
                <button
                  onClick={() => switchProvider('interakt')}
                  disabled={switching}
                  data-testid="crm-provider-interakt"
                  className={`px-3 py-1 rounded-md text-xs font-medium transition ${config?.provider === 'interakt' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}
                >
                  Interakt
                </button>
                <button
                  onClick={() => switchProvider('meta')}
                  disabled={switching || !config?.configured}
                  data-testid="crm-provider-meta"
                  title={!config?.configured ? 'Configure & test Meta first' : ''}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition ${config?.provider === 'meta' ? 'bg-emerald-500 text-white shadow' : 'text-gray-500 disabled:opacity-50'}`}
                >
                  Meta
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Webhook URL card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-600" /> Webhook URL
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <p className="text-xs text-gray-500">
              Paste this into your Meta app → WhatsApp → Configuration → Callback URL. Subscribe to <b>messages</b> and <b>message_template_status_update</b>.
            </p>
            <div className="flex gap-2 items-center bg-gray-50 border rounded-lg px-3 py-2">
              <code className="flex-1 text-xs md:text-sm break-all font-mono text-gray-700" data-testid="crm-webhook-url">
                {webhookUrl || '(REACT_APP_BACKEND_URL missing)'}
              </code>
              <Button size="sm" variant="ghost" onClick={() => copy(webhookUrl, 'Webhook URL')} data-testid="crm-copy-webhook">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Credentials form */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-600" /> Credentials
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <p className="text-xs text-gray-500">
              Grab these from your Eden WABA app in{' '}
              <a href="https://developers.facebook.com/" target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                Meta for Developers <ExternalLink className="w-3 h-3" />
              </a>
              . Fields marked with dots have a value already saved — leave them as-is if you don&apos;t want to change them.
            </p>

            <Field label="App ID"           testId="crm-field-app-id"          value={form.app_id}          onChange={(v) => setForm({ ...form, app_id: v })} />
            <Field label="App Secret"       testId="crm-field-app-secret"      value={form.app_secret}      onChange={(v) => setForm({ ...form, app_secret: v })} type="password" />
            <Field label="Phone Number ID"  testId="crm-field-phone-id"        value={form.phone_number_id} onChange={(v) => setForm({ ...form, phone_number_id: v })} />
            <Field label="WABA ID"          testId="crm-field-waba-id"         value={form.waba_id}         onChange={(v) => setForm({ ...form, waba_id: v })} />
            <Field label="Permanent Access Token" testId="crm-field-token"     value={form.access_token}    onChange={(v) => setForm({ ...form, access_token: v })} type="password" />
            <Field
              label="Webhook Verify Token"
              testId="crm-field-verify"
              value={form.verify_token}
              onChange={(v) => setForm({ ...form, verify_token: v })}
              hint="A random string you choose. Enter the same value in Meta's Webhook config."
            />

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="crm-save-config-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" onClick={test} disabled={testing || !config?.access_token} data-testid="crm-test-config-btn">
                <MessageCircle className="w-4 h-4 mr-1" /> {testing ? 'Testing…' : 'Test connection'}
              </Button>
              {config?.last_tested_at && (
                <Badge className="bg-emerald-50 text-emerald-700 ml-1" data-testid="crm-last-tested">
                  Last tested: {String(config.last_tested_at).slice(0, 16).replace('T', ' ')}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {loading && (
          <p className="text-center text-sm text-gray-400">Loading…</p>
        )}
      </div>
    </Layout>
  );
};

const Field = ({ label, value, onChange, testId, hint, type }) => (
  <div>
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <Input
      type={type || 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1"
      data-testid={testId}
    />
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

export default CRMSettingsPage;
